import type { CourseStatus, CourseAudience, ProgressStatus } from '@kennisbank/shared';

/**
 * Kleine label-chips voor de E-learning admin. Kleuren volgen DESIGN.md
 * (aqua/petrol/mosterdgeel/antraciet). Bewust losse componenten zodat de
 * cursuslijst en het dashboard exact dezelfde chips renderen.
 */

const STATUS_STYLES: Record<CourseStatus, { label: string; cls: string }> = {
  draft: { label: 'Concept', cls: 'bg-ink-50 text-ink-700 border-ink-100' },
  active: { label: 'Actief', cls: 'bg-brand-50 text-brand-800 border-brand-200' },
  archived: { label: 'Archief', cls: 'bg-ink-50 text-ink-500/70 border-ink-100' },
};

export function AdminStatusChip({ status }: { status: CourseStatus }): JSX.Element {
  const s = STATUS_STYLES[status];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

const AUDIENCE_STYLES: Record<CourseAudience, { label: string; cls: string }> = {
  iedereen: { label: 'Iedereen', cls: 'bg-brand-50 text-brand-800 border-brand-200' },
  intern: { label: 'SUPPOHRT Intern', cls: 'bg-petrol-50 text-petrol-700 border-petrol-100' },
  hoofdkantoor: { label: 'SUPPOHRT Kantoor', cls: 'bg-ink-50 text-ink-800 border-ink-100' },
};

export function AdminAudienceChip({ audience }: { audience: CourseAudience }): JSX.Element {
  const a = AUDIENCE_STYLES[audience];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${a.cls}`}
    >
      {a.label}
    </span>
  );
}

const PROGRESS_STYLES: Record<ProgressStatus, { label: string; cls: string }> = {
  bezig: { label: 'Bezig', cls: 'bg-mosterdgeel-10 text-mosterdgeel-600 border-mosterdgeel-100' },
  examenklaar: {
    label: 'Klaar voor examen',
    cls: 'bg-petrol-50 text-petrol-700 border-petrol-100',
  },
  geslaagd: { label: 'Geslaagd', cls: 'bg-brand-50 text-brand-800 border-brand-200' },
  gezakt: { label: 'Gezakt', cls: 'bg-red-50 text-red-700 border-red-200' },
};

export function AdminProgressChip({
  status,
  label,
}: {
  status: ProgressStatus;
  /** Andere tekst, bv. 'Afgerond' i.p.v. 'Geslaagd' bij een cursus zonder examen. */
  label?: string;
}): JSX.Element {
  const s = PROGRESS_STYLES[status];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${s.cls}`}
    >
      {label ?? s.label}
    </span>
  );
}
